/**
 * useProgress Hook
 *
 * Real implementation using RTK Query to fetch user progress data
 * from the practice and skills APIs.
 */

import { useMemo } from 'react';
import {
  useGetPracticeAnalyticsQuery,
  useGetPracticeStreaksQuery,
  useListPracticeSessionsQuery,
} from '../store/api/practiceApi';
import { useGetMyProgressQuery } from '../store/api/skillsApi';

export interface SessionSummary {
  id: string;
  date: string;
  scenario: string;
  persona?: string;
  clarity: number | null;
  pace: number | null;
  /** null = not measured. Never averaged or shown as 0. */
  confidence: number | null;
  /** null = not measured. Never averaged or shown as 0. */
  energy: number | null;
  /** null = not measured. */
  overallScore: number | null;
}

export interface ProgressTotals {
  sessionsCount: number;
  avgClarity: number | null;
  avgPace: number | null;
  /** Mean over sessions where it was MEASURED; null when it never was. */
  avgConfidence: number | null;
  avgEnergy: number | null;
  /** null = nothing measured. */
  avgOverallScore: number | null;
  bestStreakDays: number;
  currentStreakDays: number;
  totalPracticeMinutes: number;
}

export function useProgress() {
  // Fetch practice sessions
  const {
    data: sessionsData,
    isLoading: isSessionsLoading,
    error: sessionsError,
  } = useListPracticeSessionsQuery({ limit: 50, status: 'completed' });

  // Fetch analytics
  const {
    data: analyticsData,
    isLoading: isAnalyticsLoading,
    error: analyticsError,
  } = useGetPracticeAnalyticsQuery();

  // Fetch streaks
  const {
    data: streaksData,
    isLoading: isStreaksLoading,
    error: streaksError,
  } = useGetPracticeStreaksQuery();

  // Fetch skill progress
  const {
    data: skillProgressData,
    isLoading: isSkillProgressLoading,
  } = useGetMyProgressQuery();

  // Transform sessions to the expected format
  const sessions = useMemo<SessionSummary[]>(() => {
    if (!sessionsData?.data) return [];

    // Each session's score is the server's canonical resolution (a shadow's
    // canonical result wins; measured only). Unmeasured stays null — never 0.
    return sessionsData.data.map((session) => {
      const r = session.resolvedScore;
      const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
      return {
        id: session.id,
        date: session.createdAt,
        scenario: session.scenarioId || 'Free Practice',
        persona: undefined,
        clarity: num(r?.dimensions.clarity),
        pace: num(r?.dimensions.pace),
        confidence: num(r?.dimensions.confidence),
        energy: num(r?.dimensions.energy),
        overallScore: num(r?.overall),
      };
    });
  }, [sessionsData]);

  // Totals are the SERVER's canonical numbers (GET /practice/analytics: every
  // attempt once — practice and coaching conversations — measured only). No
  // average or count is computed here from a page of session rows.
  const totals = useMemo<ProgressTotals>(() => {
    const dims = analyticsData?.averageDimensions;
    return {
      sessionsCount: analyticsData?.totalSessions ?? 0,
      avgClarity: dims?.clarity ?? null,
      avgPace: dims?.pace ?? null,
      avgConfidence: dims?.confidence ?? null,
      avgEnergy: dims?.energy ?? null,
      avgOverallScore: analyticsData?.averageScore ?? null,
      bestStreakDays: streaksData?.longestStreak ?? 0,
      currentStreakDays: streaksData?.currentStreak ?? 0,
      totalPracticeMinutes: Math.floor((analyticsData?.totalPracticeTime ?? 0) / 60),
    };
  }, [analyticsData, streaksData]);

  // Get recent sessions for chart (last 7)
  const recentSessions = useMemo(() => {
    return [...sessions].reverse().slice(-7);
  }, [sessions]);

  // Get sessions by scenario for breakdown
  const sessionsByScenario = useMemo(() => {
    const byScenario: Record<string, SessionSummary[]> = {};
    sessions.forEach((s) => {
      if (!byScenario[s.scenario]) {
        byScenario[s.scenario] = [];
      }
      byScenario[s.scenario].push(s);
    });
    return byScenario;
  }, [sessions]);

  // Skill progress data
  const skillProgress = useMemo(() => {
    if (!skillProgressData) return null;
    return {
      totalAttempted: skillProgressData.totalObjectivesAttempted,
      totalPassed: skillProgressData.totalObjectivesPassed,
      averageAuthority: skillProgressData.averageAuthorityScore,
      byDomain: skillProgressData.progressByDomain,
    };
  }, [skillProgressData]);

  const isLoading =
    isSessionsLoading || isAnalyticsLoading || isStreaksLoading || isSkillProgressLoading;
  // The totals (sessions, streaks, minutes) come from analytics and streaks:
  // if either failed, those tiles would read a measured-looking 0. A failure
  // of any source the stats view renders is the page's error.
  const error = sessionsError ?? analyticsError ?? streaksError;

  return {
    sessions,
    recentSessions,
    sessionsByScenario,
    totals,
    skillProgress,
    isLoading,
    error,
  };
}
